/**
 * SHA-256 over fetched bytes (task 14.4).
 *
 * Every package is digested before it is parsed and before it is cached. The
 * catalog names what each package should hash to, so a truncated download, a
 * proxy that rewrote the body, or a cache entry that rotted on disk is caught
 * at the door rather than becoming a mission that fails to load with a JSON
 * error nobody can act on.
 *
 * The digest is taken over the **bytes**, not over a re-serialisation of the
 * parsed object: `JSON.parse` then `JSON.stringify` is not the identity
 * function on key order or number formatting, and a check that only holds for
 * documents this build happens to round-trip is not a check.
 */

/** Lowercase hex, which is how `catalog.json` writes a digest. */
function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Whether this environment can verify a digest at all.
 *
 * `crypto.subtle` is unavailable outside a secure context. The published
 * distribution is HTTPS-only so this is true in the shipped game; it can be
 * false on a bare-http development host, and the caller decides what to do
 * about that rather than being handed a silent pass.
 */
export function canVerify(): boolean {
  return typeof globalThis.crypto?.subtle?.digest === 'function';
}

/** The SHA-256 of `bytes`, lowercase hex. */
export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  // Copied into a fresh buffer: a `Uint8Array` may be a view onto a larger
  // buffer, and digesting the whole of that would hash bytes nobody sent.
  const exact = new Uint8Array(view);
  return hex(await globalThis.crypto.subtle.digest('SHA-256', exact));
}

/** The SHA-256 of `text` as UTF-8, which is how a package is served. */
export async function sha256OfText(text: string): Promise<string> {
  return sha256Hex(new TextEncoder().encode(text));
}

/** Thrown when fetched bytes are not the bytes the catalog named. */
export class DigestMismatchError extends Error {
  readonly url: string;
  readonly expected: string;
  readonly actual: string;

  constructor(url: string, expected: string, actual: string) {
    super(
      `The copy of ${url} this device received is not the one the catalog describes. It has been discarded rather than played.`,
    );
    this.name = 'DigestMismatchError';
    this.url = url;
    this.expected = expected;
    this.actual = actual;
  }
}

/**
 * Compares two hex digests.
 *
 * Case-insensitive, and false for an empty expectation rather than true: an
 * entry with no `sha256` is a catalog that has not been through the publish
 * script, and treating "nothing to compare" as "matches" would quietly turn
 * verification off for every mission published by hand.
 */
export function digestMatches(expected: string, actual: string): boolean {
  if (expected === '') return false;
  return expected.toLowerCase() === actual.toLowerCase();
}
